import { BoardGrid, PieceType, Side, getBoardHash } from './JanggiRules.js';

export type BeautyPoseId = 'pose1' | 'pose2' | 'pose3' | 'pose4' | 'pose5';

export interface BeautyPoseSpec {
  poseId: BeautyPoseId;
  poseTitle: string;
  poseLabel: string;
  expressionTag: string;
  moodTag: string;
  imageUrl: string;
  quote: string;
}

export interface BeautyKnightStage {
  id: string;
  stageId: string;
  chapterId: string;
  chapterNumber: number; // 1 ~ 20
  stageNumber: number; // 1 ~ 5
  globalStageNumber: number; // 1 ~ 100
  title: string;
  subtitle: string;
  poseId: BeautyPoseId;
  poseOnStart: BeautyPoseId;
  poseImageUrl: string;
  knightQuote: string;
  stageQuote: string;
  hintText: string;
  maxMovesToWin: number;
  allowHunsu: boolean;         // ★ U06: 일반 플레이에서는 1~5관문 전 구간 훈수 허용
  hardcoreNoHunsu?: boolean;   // 고난도 도전 모드(Challenge) 선택 시에만 3~5관문 훈수 제한
  boardHash?: string;          // ★ U07: 100관문 고유 시작 배치 해시
  rewardCoins: number;
  rewardGems: number;
  setupBoard: () => BoardGrid;
}

export type BeautyStageSpec = BeautyKnightStage;

export interface BeautyKnightChapter {
  id: string;
  chapterId: string;
  chapterNumber: number; // 1 ~ 20
  categoryGroup: string; // 5챕터 단위 테마 라벨
  knightName: string;
  age: number;
  title: string;
  subtitle: string;
  personality: string;
  titleBadge: string;
  tierLabel: string;
  difficultyLabel: string;
  themeColor: string;
  secretaryBuffDesc: string;
  secretaryGreeting: string;
  baseImageUrl: string;
  poses: Record<BeautyPoseId, BeautyPoseSpec>;
  stages: BeautyKnightStage[]; // 5 stages per chapter
}

function emptyGrid(): BoardGrid {
  return Array.from({ length: 10 }, () => Array.from({ length: 9 }, () => null));
}

function put(grid: BoardGrid, row: number, col: number, side: Side, type: PieceType, idx: number): void {
  grid[row][col] = {
    id: `bk_${side}_${type}_${row}_${col}_${idx}`,
    side,
    type,
  };
}

// 10가지 정통 실전 사활·묘수풀이 기보 템플릿 (챕터 및 관문 번호에 따라 기물 배치와 수순 난이도가 진화)
function buildTacticalStageBoard(chapterNum: number, stageNum: number): BoardGrid {
  const b = emptyGrid();
  const seed = ((chapterNum - 1) * 5 + (stageNum - 1)) % 10;

  switch (seed) {
    case 0: {
      // 1형: 쌍차 귀문관 돌파 (초보~중수 쾌속 외통)
      put(b, 0, 4, 'han', 'king', 1);
      put(b, 0, 3, 'han', 'sa', 2);
      put(b, 1, 5, 'han', 'sa', 3);
      put(b, 2, 4, 'han', 'ma', 4);
      put(b, 3, 2, 'han', 'jol', 5);
      put(b, 9, 4, 'cho', 'king', 6);
      put(b, 9, 3, 'cho', 'sa', 7);
      put(b, 2, 1, 'cho', 'cha', 8);
      put(b, 3, 7, 'cho', 'cha', 9);
      put(b, 4, 4, 'cho', 'po', 10);
      put(b, 3, 4, 'cho', 'jol', 11);
      break;
    }
    case 1: {
      // 2형: 중포(中包) 양차 협공진
      put(b, 1, 4, 'han', 'king', 1);
      put(b, 0, 3, 'han', 'sa', 2);
      put(b, 0, 5, 'han', 'sa', 3);
      put(b, 2, 3, 'han', 'sang', 4);
      put(b, 2, 5, 'han', 'ma', 5);
      put(b, 9, 4, 'cho', 'king', 6);
      put(b, 8, 4, 'cho', 'sa', 7);
      put(b, 4, 4, 'cho', 'po', 8);
      put(b, 2, 4, 'cho', 'ma', 9);
      put(b, 1, 1, 'cho', 'cha', 10);
      put(b, 3, 6, 'cho', 'jol', 11);
      break;
    }
    case 2: {
      // 3형: 천리독행(千里獨行) 차·마 합작살
      put(b, 0, 4, 'han', 'king', 1);
      put(b, 1, 3, 'han', 'sa', 2);
      put(b, 1, 5, 'han', 'sa', 3);
      put(b, 2, 2, 'han', 'po', 4);
      put(b, 3, 6, 'han', 'jol', 5);
      put(b, 9, 4, 'cho', 'king', 6);
      put(b, 9, 5, 'cho', 'sa', 7);
      put(b, 2, 6, 'cho', 'cha', 8);
      put(b, 3, 3, 'cho', 'ma', 9);
      put(b, 5, 4, 'cho', 'po', 10);
      put(b, 1, 2, 'cho', 'jol', 11);
      break;
    }
    case 3: {
      // 4형: 면상(面象) 파쇄진
      put(b, 1, 4, 'han', 'king', 1);
      put(b, 0, 4, 'han', 'sa', 2);
      put(b, 1, 3, 'han', 'sa', 3);
      put(b, 2, 4, 'han', 'jol', 4);
      put(b, 3, 1, 'han', 'cha', 5);
      put(b, 9, 4, 'cho', 'king', 6);
      put(b, 8, 3, 'cho', 'sa', 7);
      put(b, 8, 5, 'cho', 'sa', 8);
      put(b, 2, 0, 'cho', 'cha', 9);
      put(b, 4, 2, 'cho', 'sang', 10);
      put(b, 4, 4, 'cho', 'po', 11);
      put(b, 3, 5, 'cho', 'ma', 12);
      break;
    }
    case 4: {
      // 5형: 칠성취회(七星聚會) 변형 포위망
      put(b, 0, 4, 'han', 'king', 1);
      put(b, 0, 3, 'han', 'sa', 2);
      put(b, 1, 4, 'han', 'sa', 3);
      put(b, 2, 6, 'han', 'cha', 4);
      put(b, 4, 3, 'han', 'jol', 5);
      put(b, 9, 4, 'cho', 'king', 6);
      put(b, 9, 3, 'cho', 'sa', 7);
      put(b, 1, 7, 'cho', 'cha', 8);
      put(b, 2, 2, 'cho', 'cha', 9);
      put(b, 5, 4, 'cho', 'po', 10);
      put(b, 1, 5, 'cho', 'jol', 11);
      break;
    }
    case 5: {
      // 6형: 양포(兩包) 겹포 대공세
      put(b, 1, 4, 'han', 'king', 1);
      put(b, 0, 3, 'han', 'sa', 2);
      put(b, 0, 5, 'han', 'sa', 3);
      put(b, 3, 4, 'han', 'ma', 4);
      put(b, 2, 7, 'han', 'po', 5);
      put(b, 9, 4, 'cho', 'king', 6);
      put(b, 8, 4, 'cho', 'sa', 7);
      put(b, 5, 4, 'cho', 'po', 8);
      put(b, 7, 4, 'cho', 'po', 9);
      put(b, 2, 1, 'cho', 'cha', 10);
      put(b, 3, 5, 'cho', 'ma', 11);
      break;
    }
    case 6: {
      // 7형: 궁성 측익 기습 (초 차·병 협공)
      put(b, 0, 3, 'han', 'king', 1);
      put(b, 0, 4, 'han', 'sa', 2);
      put(b, 1, 4, 'han', 'sa', 3);
      put(b, 2, 3, 'han', 'ma', 4);
      put(b, 3, 5, 'han', 'sang', 5);
      put(b, 9, 4, 'cho', 'king', 6);
      put(b, 9, 5, 'cho', 'sa', 7);
      put(b, 2, 7, 'cho', 'cha', 8);
      put(b, 1, 2, 'cho', 'jol', 9);
      put(b, 4, 3, 'cho', 'po', 10);
      put(b, 3, 1, 'cho', 'ma', 11);
      break;
    }
    case 7: {
      // 8형: 사궁(四宮) 포위 외통진
      put(b, 1, 5, 'han', 'king', 1);
      put(b, 0, 4, 'han', 'sa', 2);
      put(b, 1, 4, 'han', 'sa', 3);
      put(b, 2, 5, 'han', 'jol', 4);
      put(b, 3, 2, 'han', 'po', 5);
      put(b, 9, 4, 'cho', 'king', 6);
      put(b, 8, 3, 'cho', 'sa', 7);
      put(b, 2, 8, 'cho', 'cha', 8);
      put(b, 3, 6, 'cho', 'ma', 9);
      put(b, 5, 5, 'cho', 'po', 10);
      put(b, 2, 4, 'cho', 'jol', 11);
      break;
    }
    case 8: {
      // 9형: 용마(龍馬) 도약살
      put(b, 0, 4, 'han', 'king', 1);
      put(b, 1, 3, 'han', 'sa', 2);
      put(b, 1, 5, 'han', 'sa', 3);
      put(b, 2, 4, 'han', 'po', 4);
      put(b, 3, 7, 'han', 'cha', 5);
      put(b, 9, 4, 'cho', 'king', 6);
      put(b, 8, 4, 'cho', 'sa', 7);
      put(b, 3, 2, 'cho', 'ma', 8);
      put(b, 3, 6, 'cho', 'ma', 9);
      put(b, 1, 0, 'cho', 'cha', 10);
      put(b, 5, 4, 'cho', 'po', 11);
      break;
    }
    default: {
      // 10형: 어전 보스 결전 (총력 외통 승부)
      put(b, 1, 4, 'han', 'king', 1);
      put(b, 0, 3, 'han', 'sa', 2);
      put(b, 0, 5, 'han', 'sa', 3);
      put(b, 2, 2, 'han', 'ma', 4);
      put(b, 2, 6, 'han', 'sang', 5);
      put(b, 3, 4, 'han', 'jol', 6);
      put(b, 9, 4, 'cho', 'king', 7);
      put(b, 9, 3, 'cho', 'sa', 8);
      put(b, 9, 5, 'cho', 'sa', 9);
      put(b, 2, 0, 'cho', 'cha', 10);
      put(b, 2, 8, 'cho', 'cha', 11);
      put(b, 5, 4, 'cho', 'po', 12);
      put(b, 3, 3, 'cho', 'ma', 13);
      break;
    }
  }

  // ★ U07: 100관문 시드 다양화 (좌우 대칭 변환 + 챕터/관문별 고유 보조 기물 배치로 100개 전 스테이지 고유 포지션 보장)
  const globalIdx = (chapterNum - 1) * 5 + (stageNum - 1); // 0 .. 99
  const variant = Math.floor(globalIdx / 10); // 0 .. 9
  const shouldMirror = variant % 2 === 1;
  const variantGroup = Math.floor(variant / 2); // 0 .. 4

  let board = b;
  if (shouldMirror) {
    const mirrored = emptyGrid();
    for (let r = 0; r < 10; r++) {
      for (let c = 0; c < 9; c++) {
        const cell = board[r][c];
        if (cell) {
          mirrored[r][8 - c] = {
            ...cell,
            id: `${cell.id}_m`
          };
        }
      }
    }
    board = mirrored;
  }

  // variantGroup (0..4)에 따라 대국 핵심 공격선에 간섭하지 않는 후방/측익 위치에 고유 보조 기물 배치
  const putIfEmpty = (r: number, c: number, side: Side, type: PieceType, tag: number) => {
    const col = shouldMirror ? 8 - c : c;
    if (!board[r][col]) {
      put(board, r, col, side, type, 100 + tag);
    }
  };

  if (variantGroup === 1) {
    putIfEmpty(6, 0, 'cho', 'jol', 1);
    putIfEmpty(3, 8, 'han', 'jol', 2);
  } else if (variantGroup === 2) {
    putIfEmpty(6, 8, 'cho', 'jol', 3);
    putIfEmpty(9, 1, 'cho', 'sang', 4);
  } else if (variantGroup === 3) {
    putIfEmpty(7, 0, 'cho', 'jol', 5);
    putIfEmpty(2, 8, 'han', 'jol', 6);
    putIfEmpty(9, 7, 'cho', 'sang', 7);
  } else if (variantGroup === 4) {
    putIfEmpty(6, 0, 'cho', 'jol', 8);
    putIfEmpty(6, 8, 'cho', 'jol', 9);
    putIfEmpty(0, 1, 'han', 'sang', 10);
  }

  return board;
}

interface RawKnightMeta {
  chapterNumber: number;
  categoryGroup: string;
  knightName: string;
  age: number;
  personality: string;
  titleBadge: string;
  tierLabel: string;
  difficultyLabel: string;
  themeColor: string;
  secretaryBuffDesc: string;
  secretaryGreeting: string;
  quotes: [string, string, string, string, string];
}

// ============================================================================
// 5챕터 단위 4대 테마 (총 20명 · 100관문)
// 대화톤: 노골적이거나 저급한 표현 없이, 예의 바르고 담백한데 묘하게 여운을 남기고
//         상대의 자존감을 세워주며 은근히 선을 타는 '진짜 고단수 여우' 화법
// ============================================================================
const RAW_KNIGHTS_20: RawKnightMeta[] = [
  // --------------------------------------------------------------------------
  // [Ch.01 ~ Ch.05] 한국 엘리트 커리어우먼 글래머 절세미녀 5인
  // --------------------------------------------------------------------------
  {
    chapterNumber: 1,
    categoryGroup: '한국 커리어우먼',
    knightName: '한채린',
    age: 26,
    personality: '단정한 눈웃음 · 은근한 여백',
    titleBadge: '회장실 수석 수행비서',
    tierLabel: '15급',
    difficultyLabel: '입문 ★☆☆☆☆',
    themeColor: '#f43f5e',
    secretaryBuffDesc: '대국 승리 엽전 +10% · 일일 무료 도전권 +1',
    secretaryGreeting: '일정은 다 비워두었어요. 오늘은 다른 생각 말고 저랑만 두시는 거예요.',
    quotes: [
      '원래 첫 수부터 이렇게 사람 긴장하게 만드시는 편이에요? 아니면 저한테만 그러시는 건가요.',
      '방금 그 수, 겉으론 배려해주시는 척하면서 길은 다 막아두셨네요. 조금 얄미울 뻔했어요.',
      '자꾸 그렇게 제 생각보다 한 발 먼저 와 계시면... 제가 따라갈 수가 없잖아요.',
      '이쯤 되면 살짝 모른 척 넘어가 주실 줄 알았는데, 생각보다 훨씬 단호하시네요.',
      '졌어요. 원래 지는 거 정말 싫어하는데... 이상하게 오늘은 기분이 나쁘지 않네요. 내일도 비워둘게요.',
    ],
  },
  {
    chapterNumber: 2,
    categoryGroup: '한국 커리어우먼',
    knightName: '서유나',
    age: 27,
    personality: '차분한 아나운서 · 밤의 반전',
    titleBadge: '심야 마감 뉴스 앵커',
    tierLabel: '13급',
    difficultyLabel: '입문 ★☆☆☆☆',
    themeColor: '#10b981',
    secretaryBuffDesc: '대국 승리 엽전 +12% · 훈수 비급 효율 상승',
    secretaryGreeting: '카메라 꺼진 지 오래예요. 지금부턴 방송 톤 말고 편하게 말씀하셔도 돼요.',
    quotes: [
      '생방송보다 지금이 더 떨리네요. 맞은편에서 그렇게 조용히 쳐다보시니까요.',
      '말씀은 참 차분하게 하시는데, 두시는 수는 하나도 안 차분하시네요.',
      '잠깐만요, 지금 장기판 보시는 거 맞죠? 시선이 자꾸 다른 데 머무시는 것 같아서요.',
      '이렇게 도망갈 자리도 안 남겨두시면, 저는 어쩌라는 말씀이세요.',
      '완패네요. 오늘 방송 끝나고 시간 괜찮으시면, 복기 핑계로 한 잔 사주실래요?',
    ],
  },
  {
    chapterNumber: 3,
    categoryGroup: '한국 커리어우먼',
    knightName: '강세라',
    age: 28,
    personality: '도도한 지성미 · 계산 밖의 설렘',
    titleBadge: '여의도 VIP 전담 프라이빗 뱅커',
    tierLabel: '11급',
    difficultyLabel: '초급 ★★☆☆☆',
    themeColor: '#3b82f6',
    secretaryBuffDesc: '대국 승리 엽전 +15% · 위기감지 수호 보너스',
    secretaryGreeting: '수익률 계산은 끝났어요. 오늘 가장 확실한 투자는 당신 편에 서는 거네요.',
    quotes: [
      '보통 제 고객분들은 첫 판에 그렇게 과감하게 안 들어오시거든요. 조금 의외네요.',
      '손익 계산이 원래 이렇게 빠르세요? 제가 준비한 패가 자꾸 무색해지잖아요.',
      '자켓 좀 벗어도 될까요. 방 안이 더운 건지, 당신 수읽기 때문에 열이 오르는 건지 모르겠네요.',
      '사람 마음을 이렇게 코너로 몰아넣고도 표정 하나 안 변하시네요. 참 나쁜 분이에요.',
      '제 계산이 완전히 틀렸네요. 이번 계약은 조건 없이 당신 뜻대로 따를게요.',
    ],
  },
  {
    chapterNumber: 4,
    categoryGroup: '한국 커리어우먼',
    knightName: '윤지수',
    age: 29,
    personality: '냉철한 변호사 · 흔들리는 눈빛',
    titleBadge: '서초동 대형 로펌 파트너 변호사',
    tierLabel: '9급',
    difficultyLabel: '초급 ★★☆☆☆',
    themeColor: '#f59e0b',
    secretaryBuffDesc: '대국 승리 엽전 +18% · 연승 포인트 보너스',
    secretaryGreeting: '법정 밖에서까지 누군가에게 설득당한 건 처음이에요. 책임지셔야 해요.',
    quotes: [
      '변론할 기회는 주시면서 두시는 거죠? 첫 수부터 숨통을 조여오시네요.',
      '논리적인 척하시지만 방금 그 수는 꽤 집요했어요. 싫다는 뜻은 아니에요.',
      '안경 너머로 안 보일 줄 알았는데, 제 표정 변화를 다 읽고 계셨나 봐요.',
      '여기서 한 수만 더 들어오시면 저 정말 할 말이 없어지는데... 그래도 두실 건가요.',
      '항소 포기할게요. 오늘 밤은 당신이 정한 규칙대로 움직여 드릴게요.',
    ],
  },
  {
    chapterNumber: 5,
    categoryGroup: '한국 커리어우먼',
    knightName: '백하연',
    age: 30,
    personality: '절세미녀 총지배인 · 우아한 밀당',
    titleBadge: '특급 호텔 최연소 총지배인 (Ch.05 보스)',
    tierLabel: '7급',
    difficultyLabel: '중급 ★★☆☆☆',
    themeColor: '#0ea5e9',
    secretaryBuffDesc: '대국 승리 엽전 +20% · 품격도 +100 보너스',
    secretaryGreeting: '펜트하우스 라운지 비워두었습니다. 오늘 밤은 총지배인이 아니라 당신 개인 비서예요.',
    quotes: [
      '수많은 VIP를 모셔봤지만, 앉자마자 공기부터 바꾸는 분은 오랜만이네요.',
      '급하게 두지 않으셔도 돼요. 오늘 밤 제 마지막 일정은 당신이니까요.',
      '그렇게 여유롭게 웃으시면서 가장 아픈 곳만 찌르시네요. 원래 그런 스타일이세요?',
      '스위트룸 키를 걸고 한 내기였는데... 일부러 저를 이기려고 작정하고 오신 것 같네요.',
      '완벽하게 지고 말았네요. 약속대로 이 라운지의 밤과 저는 이제 당신 소관이에요.',
    ],
  },

  // --------------------------------------------------------------------------
  // [Ch.06 ~ Ch.10] 일본 프라이빗 미녀 & 절세 글래머 5인
  // --------------------------------------------------------------------------
  {
    chapterNumber: 6,
    categoryGroup: '일본 절세미녀',
    knightName: '사토 미유',
    age: 25,
    personality: '교토의 정적 · 나긋한 유혹',
    titleBadge: '교토 전통 료칸의 젊은 여주인',
    tierLabel: '6급',
    difficultyLabel: '중급 ★★★☆☆',
    themeColor: '#22c55e',
    secretaryBuffDesc: '대국 승리 엽전 +22% · 상수(象手) 비급 효율 강화',
    secretaryGreeting: '온천물은 알맞게 데워두었어요. 대국이 끝나면 천천히 피로를 푸세요.',
    quotes: [
      '먼 길 오시느라 고생 많으셨어요. 차가 식기 전에 가볍게 한 수 두실까요.',
      '조용한 분인 줄 알았는데, 장기알을 놓으실 땐 손끝이 참 단단하시네요.',
      '기모노 소매가 자꾸 신경 쓰이네요... 그렇게 가까이서 내려다보시면 부끄러워요.',
      '방문 너머로 밤바람이 차가운데, 조금만 더 천천히 끝내주시면 안 될까요.',
      '당신 수읽기엔 당해낼 재간이 없네요. 오늘 밤 료칸의 모든 시중은 제가 직접 들게요.',
    ],
  },
  {
    chapterNumber: 7,
    categoryGroup: '일본 절세미녀',
    knightName: '시라카와 레이',
    age: 27,
    personality: '정중한 복종 · 은밀한 시선',
    titleBadge: '도쿄 마루노우치 외국계 임원 비서',
    tierLabel: '5급',
    difficultyLabel: '중급 ★★★☆☆',
    themeColor: '#be123c',
    secretaryBuffDesc: '대국 승리 엽전 +25% · 천안통 투시 보너스',
    secretaryGreeting: '야근이 길어져도 괜찮아요. 당신 옆자리에 앉아 있는 시간이 제일 조용하고 좋거든요.',
    quotes: [
      '실례가 안 된다면, 오늘은 직함 빼고 편하게 불러주셔도 괜찮아요.',
      '어머, 제가 방금 한숨 쉰 거 들으셨어요? 나쁜 뜻이 아니라... 수가 너무 깊어서요.',
      '블라우스 단추가 살짝 당기네요. 당신이 자꾸 숨 막히는 수만 골라 두셔서 그런가 봐요.',
      '여기서 더 몰아붙이시면, 저도 비서로서의 평정심을 유지하기가 힘들어져요.',
      '결국 당신 뜻대로 되었네요. 도쿄에 계시는 동안은 제 퇴근 시간도 당신께 맡길게요.',
    ],
  },
  {
    chapterNumber: 8,
    categoryGroup: '일본 절세미녀',
    knightName: '타카하시 아오이',
    age: 28,
    personality: '긴자의 품격 · 남자를 세워주는 여우',
    titleBadge: '긴자 프라이빗 클럽 오너 마담',
    tierLabel: '4급',
    difficultyLabel: '중급 ★★★☆☆',
    themeColor: '#f97316',
    secretaryBuffDesc: '대국 승리 엽전 +28% · 도전권 회복 보너스',
    secretaryGreeting: '오늘 밤 가게 예약은 전부 거절했어요. 당신 한 분만 모시는 게 훨씬 즐거우니까요.',
    quotes: [
      '긴자에서 수많은 남자를 봤지만, 말없이 판을 지배하는 분은 흔치 않거든요.',
      '잔을 채워드릴 때마다 손목을 스치듯 보시는데... 저를 흔들려는 작전이신가요.',
      '참 묘하네요. 분명 제가 불리한 판인데, 자꾸 당신 페이스에 끌려가고 싶어져요.',
      '이렇게 다 알고 있다는 듯이 미소 지으시면, 여자는 무장해제될 수밖에 없어요.',
      '깨끗이 인정할게요. 오늘부터 아오이는 긴자가 아니라 당신만을 위한 사람이에요.',
    ],
  },
  {
    chapterNumber: 9,
    categoryGroup: '일본 절세미녀',
    knightName: '후지이 노조미',
    age: 29,
    personality: '청초한 외모 · 반전 글래머',
    titleBadge: '오사카 재벌가 출신 수석 큐레이터',
    tierLabel: '3급',
    difficultyLabel: '중고급 ★★★☆☆',
    themeColor: '#14b8a6',
    secretaryBuffDesc: '대국 승리 엽전 +30% · 명품관 품격도 +150',
    secretaryGreeting: '전시관 문을 닫고 단둘이 남았을 때의 그 정적, 저는 그때가 제일 설레더라고요.',
    quotes: [
      '조용한 곳에서 단둘이 마주 앉으니까, 장기알 놓는 소리마저 유난히 크게 들리네요.',
      '제 수비를 그렇게 빤히 들여다보시면... 마치 옷차림을 들킨 것처럼 민망해져요.',
      '원래 그렇게 상대가 가장 아끼는 것부터 조용히 가져가시는 편인가요.',
      '이제 한 걸음만 더 오시면 정말 막을 수가 없는데... 그래도 안 멈추실 거죠?',
      '당신의 안목과 수읽기에 완전히 매료됐어요. 가장 아끼는 컬렉션 옆에 저도 두시겠어요?',
    ],
  },
  {
    chapterNumber: 10,
    categoryGroup: '일본 절세미녀',
    knightName: '쿠죠 마리카',
    age: 31,
    personality: '도쿄 최상류층 · 절세 글래머 여왕',
    titleBadge: '아자부주반 프라이빗 살롱 오너 (Ch.10 보스)',
    tierLabel: '2급',
    difficultyLabel: '중고급 ★★★★☆',
    themeColor: '#eab308',
    secretaryBuffDesc: '대국 승리 엽전 +35% · 잭팟 보상 보너스',
    secretaryGreeting: '누구에게도 먼저 고개 숙인 적 없었는데... 당신 앞에서는 그게 참 자연스럽네요.',
    quotes: [
      '제 살롱에 초대받은 분들은 보통 저를 이기려 하지 않던데, 당신은 눈빛부터 다르네요.',
      '부드러운 매너 뒤에 그렇게 서늘한 승부수를 숨기고 계실 줄은 몰랐어요.',
      '드레스 어깨선이 살짝 흘러내렸나요? 당신 수를 읽느라 신경 쓸 겨를도 없었네요.',
      '이 판을 내어주면 제 자존심도 함께 내어드리는 셈인데... 이상하게 아깝지가 않네요.',
      '당신이 이겼어요. 도쿄에서 가장 도도하던 여자를 이렇게 단숨에 길들이시네요.',
    ],
  },

  // --------------------------------------------------------------------------
  // [Ch.11 ~ Ch.15] 금발(Blonde) 서구권 절세미녀 슈퍼 글래머 5인
  // --------------------------------------------------------------------------
  {
    chapterNumber: 11,
    categoryGroup: '금발 절세미녀',
    knightName: '엘레나 보르코바',
    age: 25,
    personality: '플래티넘 블론드 · 차가운 미소 뒤의 열기',
    titleBadge: '모스크바 출신 하이패션 모델 & 기원 애호가',
    tierLabel: '1급',
    difficultyLabel: '고급 ★★★★☆',
    themeColor: '#ec4899',
    secretaryBuffDesc: '대국 승리 엽전 +38% · 복기 연구실 보너스',
    secretaryGreeting: '밖이 아무리 추워도 상관없어요. 당신 옆에 서 있으면 늘 계절을 잊어버리거든요.',
    quotes: [
      '한국 남자는 원래 이렇게 눈빛으로 먼저 사람을 묶어두고 시작하나요?',
      '체스보다 장기가 더 야릇하네요. 길이 막힌 줄 알았는데 포(包)가 넘어오니까요.',
      '차가워 보인다는 말을 자주 듣는데, 당신 앞에서는 표정 관리가 잘 안 되네요.',
      '그렇게 침착한 얼굴로 제 마지막 퇴로까지 막으시면... 반칙 아닌가요.',
      '당신 수읽기 앞에서는 어떤 포즈도 소용없네요. 오늘부터 당신 전속으로 남을게요.',
    ],
  },
  {
    chapterNumber: 12,
    categoryGroup: '금발 절세미녀',
    knightName: '클로이 로랑',
    age: 27,
    personality: '골든 블론드 · 프렌치 시크 밀당',
    titleBadge: '파리 오트쿠튀르 VIP 디렉터',
    tierLabel: '1단',
    difficultyLabel: '고급 ★★★★☆',
    themeColor: '#8b5cf6',
    secretaryBuffDesc: '대국 승리 엽전 +40% · 한수 무르기 효율 상승',
    secretaryGreeting: '향수 냄새가 진하다고요? 당신이 가까이 다가왔을 때만 나도록 살짝 뿌린 건데.',
    quotes: [
      '서두르지 말아요. 좋은 와인과 좋은 승부는 원래 천천히 음미하는 법이잖아요.',
      '방금 그 수, 꽤 세련됐어요. 파리에서도 그렇게 매너 있게 압박하는 남자는 드물거든요.',
      '고개를 살짝 기울인 건 수가 안 보여서가 아니라, 집중하는 당신 얼굴이 보기 좋아서예요.',
      '실크 드레스가 괜히 답답하게 느껴질 만큼, 당신의 공세가 너무 밀도 있네요.',
      '항복할게요. 사람을 이렇게 애타게 만들고 결국 이겨버리는 법은 어디서 배우셨어요?',
    ],
  },
  {
    chapterNumber: 13,
    categoryGroup: '금발 절세미녀',
    knightName: '빅토리아 스털링',
    age: 29,
    personality: '월스트리트 금발 여신 · 당당한 유혹',
    titleBadge: '뉴욕 프라이빗 헤지펀드 디렉터',
    tierLabel: '2단',
    difficultyLabel: '고급 ★★★★☆',
    themeColor: '#06b6d4',
    secretaryBuffDesc: '대국 승리 엽전 +42% · 품격도 +200 보너스',
    secretaryGreeting: '월스트리트에서도 당신 같은 승부사는 못 봤어요. 제 포트폴리오 1순위는 이제 당신이에요.',
    quotes: [
      '딜(Deal)을 시작해 볼까요. 참고로 저는 쉽게 넘어가는 상대에는 흥미가 없답니다.',
      '흥미롭네요. 리스크를 피하는 줄 알았는데, 가장 큰 판돈을 노리고 계셨군요.',
      '자꾸 그렇게 제 의도를 먼저 꿰뚫어 보시면, 제가 숨겨둔 패가 부끄러워지잖아요.',
      '협상 테이블에서도 이렇게까지 밀려본 적은 없는데... 심장이 조금 빨라지네요.',
      '완벽한 클로징이네요. 오늘부터 제 시간과 충성심은 전부 당신 계좌로 옮겨둘게요.',
    ],
  },
  {
    chapterNumber: 14,
    categoryGroup: '금발 절세미녀',
    knightName: '소피아 발렌티',
    age: 28,
    personality: '밀라노 블론드 · 깊은 와인빛 눈맞춤',
    titleBadge: '밀라노 프라이빗 소믈리에 & 살롱 호스트',
    tierLabel: '3단',
    difficultyLabel: '명인 ★★★★☆',
    themeColor: '#a855f7',
    secretaryBuffDesc: '대국 승리 엽전 +45% · 명인 훈수 위력 강화',
    secretaryGreeting: '오늘 밤 코르크를 딴 빈티지 와인은 두 잔뿐이에요. 끝까지 저와 함께해 주실 거죠?',
    quotes: [
      '첫 모금은 부드러운데 목 넘김이 꽤 묵직하네요... 당신이 두는 수가 딱 그래요.',
      '잔을 돌리는 제 손끝만 보지 마시고, 판 위에 깔아둔 제 마(馬)도 봐주시겠어요?',
      '취기가 오르는 건 와인 때문이 아니라, 한 치의 오차도 없는 당신 눈빛 때문인 것 같아요.',
      '여기서 한 수만 더 들어오시면, 오늘 밤은 저도 브레이크를 걸 자신이 없어요.',
      '건배해요. 저를 이렇게 단숨에 무너뜨린 분은 당신이 처음이니까요.',
    ],
  },
  {
    chapterNumber: 15,
    categoryGroup: '금발 절세미녀',
    knightName: '이사벨라 폰타나',
    age: 31,
    personality: '모나코의 금발 여왕 · 슈퍼 글래머 보스',
    titleBadge: '모나코 로열 카지노 VIP 총괄 호스트 (Ch.15 보스)',
    tierLabel: '4단',
    difficultyLabel: '명인 ★★★★☆',
    themeColor: '#ef4444',
    secretaryBuffDesc: '대국 승리 엽전 +48% · 속공 보너스',
    secretaryGreeting: '모나코의 모든 조명이 꺼져도, 당신이 부르시면 언제든 가장 화려한 모습으로 서 있을게요.',
    quotes: [
      '로열 테이블에 앉으신 걸 환영해요. 오늘 밤 가장 비싼 배당은 바로 저랍니다.',
      '블러핑 한 번 없이 정면으로 들어오시네요. 남자가 그렇게 여유로우면 반칙인데.',
      '가운 어깨끈이 내려간 줄도 몰랐네요. 당신 수읽기가 워낙 숨 가쁘게 몰아쳐서요.',
      '이 마지막 관문까지 넘으시면, 저는 더 이상 호스트가 아니라 당신의 전리품이 돼요.',
      '올인, 그리고 당신의 승리네요. 모나코의 여왕을 가지신 기분이 어떠신가요.',
    ],
  },

  // --------------------------------------------------------------------------
  // [Ch.16 ~ Ch.20] 30대 농익은 매력의 미시(숙녀/사모님) 절세미녀 글래머 5인 (최대 39세)
  // --------------------------------------------------------------------------
  {
    chapterNumber: 16,
    categoryGroup: '30대 절세 미시',
    knightName: '송연우',
    age: 34,
    personality: '30대 중반 미시 · 외로운 주말의 여백',
    titleBadge: '청담동 프라이빗 갤러리 관장',
    tierLabel: '5단',
    difficultyLabel: '명인 ★★★★★',
    themeColor: '#d97706',
    secretaryBuffDesc: '대국 승리 엽전 +52% · 천안통 투시 강화',
    secretaryGreeting: '어린 친구들처럼 시끄럽게 굴진 않아요. 그저 당신이 필요할 때 가장 편안한 자리가 되어드릴게요.',
    quotes: [
      '나이가 조금 있다 보니, 이제는 말 많은 사람보다 당신처럼 손끝이 묵직한 분이 좋더라고요.',
      '주말 저녁에 갤러리에 혼자 남아 있으면 꽤 적적했거든요. 오늘 와주셔서 다행이에요.',
      '어머, 그렇게 빤히 보지 마세요. 아직은 젊은 애들 못지않다는 소리 듣긴 하지만... 부끄럽네요.',
      '이렇게까지 제 빈틈을 정확히 파고드시면, 저도 어른스러운 척하기가 힘들어지잖아요.',
      '오랜만에 가슴이 뛰는 승부였어요. 오늘 밤은 집에 일찍 들어가기 싫어지네요.',
    ],
  },
  {
    chapterNumber: 17,
    categoryGroup: '30대 절세 미시',
    knightName: '오수진',
    age: 35,
    personality: '30대 중반 글래머 미시 · 탄탄한 자기관리와 여유',
    titleBadge: '한남동 프라이빗 스튜디오 & 와인바 원장',
    tierLabel: '6단',
    difficultyLabel: '명인 ★★★★★',
    themeColor: '#10b981',
    secretaryBuffDesc: '대국 승리 엽전 +55% · 위기감지 수호 강화',
    secretaryGreeting: '운동으로 다져둔 체력이라 지치지 않아요. 오늘 밤 몇 판이든 끝까지 상대해 드릴게요.',
    quotes: [
      '20대 때는 몰랐는데, 진짜 매력적인 남자는 판 위에서 서두르는 법이 없더라고요. 딱 당신처럼요.',
      '자세가 흐트러진 게 아니라, 당신 수가 워낙 깊어서 조금 가까이 기대어 본 거예요.',
      '몸매 관리보다 어려운 게 당신 앞에서 평정심 유지하는 거네요. 자꾸 호흡이 흐트러져요.',
      '살짝 봐주실 법도 한데 끝까지 밀어붙이시네요... 근데 이상하게 그게 더 마음에 들어요.',
      '완전히 넘어갔네요. 누나라고 부르셔도 좋고, 편한 대로 부르세요. 이제 당신 편이니까요.',
    ],
  },
  {
    chapterNumber: 18,
    categoryGroup: '30대 절세 미시',
    knightName: '차명희',
    age: 37,
    personality: '30대 후반 성북동 사모님 · 정숙함 뒤의 일탈',
    titleBadge: '성북동 프라이빗 다실(茶室) 안주인',
    tierLabel: '7단',
    difficultyLabel: '국수 ★★★★★',
    themeColor: '#dc2626',
    secretaryBuffDesc: '대국 승리 엽전 +60% · 품격도 +300 보너스',
    secretaryGreeting: '늘 남의 시선만 신경 쓰며 살았는데... 당신 앞에서는 그냥 여자로 앉아 있고 싶어지네요.',
    quotes: [
      '이런 늦은 시간에 손님을 들인 건 처음이에요. 그만큼 당신과의 대국이 기다려졌나 봐요.',
      '찻잔을 건네는 손끝이 살짝 떨렸다면... 못 본 척 넘어가 주시겠어요?',
      '평생 정숙하게만 살아왔는데, 당신이 두시는 수를 보면 자꾸 선을 넘고 싶은 기분이 들어요.',
      '여기서 무너지면 정말 돌이킬 수 없는데... 당신은 알고도 그렇게 두시는 거죠.',
      '졌네요. 성북동 저택의 체면도 오늘 밤만큼은 다 잊을게요. 조금 더 머물다 가세요.',
    ],
  },
  {
    chapterNumber: 19,
    categoryGroup: '30대 절세 미시',
    knightName: '정혜란',
    age: 38,
    personality: '30대 후반 완숙미 · 남자를 꿰뚫는 농염함',
    titleBadge: '평창동 부티크 호텔 & 라운지 대표',
    tierLabel: '8단',
    difficultyLabel: '국수 ★★★★★',
    themeColor: '#6366f1',
    secretaryBuffDesc: '대국 승리 엽전 +70% · 옥패 보너스 증폭',
    secretaryGreeting: '굳이 말씀 안 하셔도 지금 뭘 원하시는지 다 알아요. 연륜이라는 게 괜히 있는 게 아니거든요.',
    quotes: [
      '어린 아가씨들 상대로는 심심하셨죠? 제 앞에서는 숨기지 않고 본실력을 다 보이셔도 돼요.',
      '그렇게 점잖은 얼굴로 가장 치명적인 자리만 골라 짚으시네요. 참 매력적인 나쁜 버릇이에요.',
      '가운을 살짝 고쳐 입었을 뿐인데 시선이 흔들리시네요. 후훗, 아직은 봐줄 만한가 봐요.',
      '원래는 제가 남자를 리드하는 편인데, 당신한테는 자꾸 끌려다니게 되네요.',
      '기분 좋은 패배예요. 제가 가진 모든 여유와 밤의 시간을 이제 당신께 맞춰 드릴게요.',
    ],
  },
  {
    chapterNumber: 20,
    categoryGroup: '30대 절세 미시',
    knightName: '선우화영',
    age: 39,
    personality: '백옥 피부와 신비로운 눈빛 · 고혹적 기품의 절세 미시 여제',
    titleBadge: '천하장기 프라이빗 영빈관 안주인 (Ch.20 최종 보스)',
    tierLabel: '9단',
    difficultyLabel: '어전신선 ★★★★★',
    themeColor: '#f59e0b',
    secretaryBuffDesc: '대국 승리 엽전 +100% · 품격도 +500 · 영빈관 여제 가호',
    secretaryGreeting: '스무 명의 여자를 모두 지나 결국 제 방까지 오셨군요. 이제 아무 데도 안 보내드릴 생각이에요.',
    quotes: [
      '여기까지 오시느라 수고하셨어요. 마지막 대국은 승패보다, 우리가 얼마나 깊어질 수 있는지의 문제랍니다.',
      '맑은 물처럼 조용히 두시는 줄 알았는데, 단 한 수로 여자의 호흡을 멈추게 하시네요.',
      '옷매무새가 조금 흐트러져도 이해해 주세요. 당신의 기세를 받아내기가 생각보다 훨씬 벅차네요.',
      '외통수네요. 도망칠 곳은 남기지 않으셨지만, 이상하게도 가장 안전한 품에 갇힌 기분이에요.',
      '당신이 천하제일이에요. 영빈관의 문은 잠가두었어요... 오늘 밤부터 저는 오직 당신 한 사람만의 여자예요.',
    ],
  },
];

// 1~5단 스테이지별로 같은 얼굴이지만 표정·자세·의상이 점점 더 과감하고 섹시해지는 구도 라벨
const POSE_TITLES: Record<BeautyPoseId, { title: string; tag: string }> = {
  pose1: { title: '1단: 단정한 첫인상 · 포멀 룩', tag: '제1관문 · 은은한 눈웃음' },
  pose2: { title: '2단: 자켓을 벗은 실크 · 스치는 시선', tag: '제2관문 · 고개 기울인 미소' },
  pose3: { title: '3단: 슬림핏 드레스 · 매혹의 옆선', tag: '제3관문 · 짙어지는 긴장감' },
  pose4: { title: '4단: 딥 브이넥 라운지 · 흔들리는 숨결', tag: '제4관문 · 달아오른 홍조' },
  pose5: { title: '5단: 시크릿 나이트 가운 · 완전한 허락', tag: '제5관문 · ★5 전속 비서 영입' },
};

const STAGE_TITLES: [string, string, string, string, string] = [
  '제1국: 단정한 첫인상과 탐색전',
  '제2국: 좁혀지는 거리와 눈맞춤',
  '제3국: 자켓을 벗은 진검승부',
  '제4국: 흔들리는 호흡과 외통 위기',
  '제5국: ★5 전속 비서 심야 최종전',
];

export const BEAUTY_KNIGHT_CHAPTERS: BeautyKnightChapter[] = RAW_KNIGHTS_20.map((meta) => {
  const numStr = String(meta.chapterNumber).padStart(2, '0');
  const chapterId = `ch_${numStr}`;
  const baseImageUrl = `/assets/images/knights/knight_${numStr}_base.png`;

  const poseIds: BeautyPoseId[] = ['pose1', 'pose2', 'pose3', 'pose4', 'pose5'];
  const poses = {} as Record<BeautyPoseId, BeautyPoseSpec>;

  poseIds.forEach((pId, idx) => {
    poses[pId] = {
      poseId: pId,
      poseTitle: POSE_TITLES[pId].title,
      poseLabel: POSE_TITLES[pId].title,
      expressionTag: POSE_TITLES[pId].tag,
      moodTag: POSE_TITLES[pId].tag,
      imageUrl: `/assets/images/knights/knight_${numStr}_${pId}.png`,
      quote: meta.quotes[idx],
    };
  });

  const stages: BeautyKnightStage[] = poseIds.map((pId, idx) => {
    const stageNum = idx + 1;
    const globalStageNumber = (meta.chapterNumber - 1) * 5 + stageNum;
    const maxMoves = Math.max(5, 11 - Math.floor(meta.chapterNumber / 4) - Math.floor(stageNum / 2));
    const stageId = `${chapterId}_st_${stageNum}`;

    const initialBoard = buildTacticalStageBoard(meta.chapterNumber, stageNum);
    return {
      id: stageId,
      stageId,
      chapterId,
      chapterNumber: meta.chapterNumber,
      stageNumber: stageNum,
      globalStageNumber,
      title: STAGE_TITLES[idx],
      subtitle: `[${meta.categoryGroup}] ${meta.knightName} (${meta.age}세 · ${meta.titleBadge}) · ${stageNum}/5 관문`,
      poseId: pId,
      poseOnStart: pId,
      poseImageUrl: poses[pId].imageUrl,
      knightQuote: meta.quotes[idx],
      stageQuote: meta.quotes[idx],
      hintText:
        stageNum <= 2
          ? '초 차(車)와 포(包)의 정면 장군 길을 먼저 확인하세요! (훈수·무르기·+3수 연장 모두 사용 가능)'
          : '차·마·포 협공으로 궁성 측면을 노리세요! 막히면 [훈수], [한수 무르기], [+3수 연장]을 활용하세요.',
      maxMovesToWin: maxMoves,
      // ★ U06: 일반 플레이에서는 1~5관문 전 구간에서 훈수를 제한하지 않고 허용!
      allowHunsu: true,
      hardcoreNoHunsu: stageNum >= 3,
      boardHash: getBoardHash(initialBoard, 'cho'),
      rewardCoins: 1500 + meta.chapterNumber * 350 + stageNum * 250,
      rewardGems: 15 + meta.chapterNumber * 4 + (stageNum === 5 ? 35 : 5),
      setupBoard: () => buildTacticalStageBoard(meta.chapterNumber, stageNum),
    };
  });

  return {
    id: chapterId,
    chapterId,
    chapterNumber: meta.chapterNumber,
    categoryGroup: meta.categoryGroup,
    knightName: meta.knightName,
    age: meta.age,
    title: meta.titleBadge,
    subtitle: meta.difficultyLabel,
    personality: meta.personality,
    titleBadge: meta.titleBadge,
    tierLabel: meta.tierLabel,
    difficultyLabel: meta.difficultyLabel,
    themeColor: meta.themeColor,
    secretaryBuffDesc: meta.secretaryBuffDesc,
    secretaryGreeting: meta.secretaryGreeting,
    baseImageUrl,
    poses,
    stages,
  };
});

export function getBeautyChapterById(chapterId: string): BeautyKnightChapter {
  return BEAUTY_KNIGHT_CHAPTERS.find((c) => c.id === chapterId || c.chapterId === chapterId) || BEAUTY_KNIGHT_CHAPTERS[0];
}

export function getBeautyStageById(stageId: string): { chapter: BeautyKnightChapter; stage: BeautyKnightStage } | null {
  for (const ch of BEAUTY_KNIGHT_CHAPTERS) {
    const st = ch.stages.find((s) => s.stageId === stageId || s.id === stageId);
    if (st) return { chapter: ch, stage: st };
  }
  return null;
}

export function getChapterClearedStars(
  chapterOrId: BeautyKnightChapter | string,
  clearedStageIds: string[]
): number {
  const ch = typeof chapterOrId === 'string' ? getBeautyChapterById(chapterOrId) : chapterOrId;
  return ch.stages.filter((st) => clearedStageIds.includes(st.stageId)).length;
}

/**
 * ★ U05: 기본 비서 '한채린(ch_01)'은 시작 즉시 동행 가능하며,
 * 추가 비서(ch_02~ch_20)는 1관문 승리(별 1개 이상)만으로 대국 동행 해금!
 * (2~5관문 승리는 해당 캐릭터의 화보 포즈 pose2~pose5를 순차 해금)
 */
export function canHireChapterSecretary(
  chapterOrId: BeautyKnightChapter | string,
  clearedStageIds: string[]
): boolean {
  const ch = typeof chapterOrId === 'string' ? getBeautyChapterById(chapterOrId) : chapterOrId;
  if (ch.id === 'ch_01' || ch.chapterNumber === 1) return true;
  return getChapterClearedStars(ch, clearedStageIds) >= 1;
}

export function canEquipChapterSecretary(
  chapterOrId: BeautyKnightChapter | string,
  clearedStageIds: string[]
): boolean {
  return canHireChapterSecretary(chapterOrId, clearedStageIds);
}

export function getUnlockedPosesForChapter(
  chapterOrId: BeautyKnightChapter | string,
  clearedStageIds: string[]
): BeautyPoseId[] {
  const ch = typeof chapterOrId === 'string' ? getBeautyChapterById(chapterOrId) : chapterOrId;
  const unlocked: BeautyPoseId[] = [];
  const poseOrder: BeautyPoseId[] = ['pose1', 'pose2', 'pose3', 'pose4', 'pose5'];

  ch.stages.forEach((st, idx) => {
    if (clearedStageIds.includes(st.stageId) || (ch.id === 'ch_01' && idx === 0)) {
      unlocked.push(poseOrder[idx]);
    }
  });
  return unlocked;
}

export function canUseHunsuInBeautyStage(
  stage: BeautyKnightStage,
  playMode: 'normal' | 'hardcore' = 'normal'
): boolean {
  if (playMode === 'hardcore') {
    return !stage.hardcoreNoHunsu;
  }
  return Boolean(stage.allowHunsu);
}

export function getTotalBeautyStars(clearedStageIds: string[]): number {
  let total = 0;
  for (const ch of BEAUTY_KNIGHT_CHAPTERS) {
    total += getChapterClearedStars(ch, clearedStageIds);
  }
  return total;
}
